const dbName = 'fileUploadDB';
const dbVersion = 1;
let db;
const chunkSize = 1024 * 1024; // 1MB chunk size (adjust as needed)

/**
 * Allocates the upload id whose numeric prefix the API parses as the chunk-set key
 * (`long.Parse` of everything before the first underscore).
 *
 * The old scheme was `${Math.floor(Math.random() * 100000)}${Date.now()}`. Files are added inside
 * one synchronous forEach, so Date.now() is IDENTICAL for every file in a batch and uniqueness
 * rested entirely on the 5-digit random - a birthday problem over 100k buckets, which for a
 * 218-file batch is a ~21% chance of at least one collision. Clinics routinely send batches that
 * size. Two files sharing an id share one server-side chunk set, so their chunks overwrite and
 * interleave into each other's DICOM; three production radiographs ended up unrecoverable that way.
 *
 * This counter is monotonic within a tab, so a batch can never collide with itself, and starts at a
 * random offset so two tabs are unlikely to align. 13-digit ms + 6-digit sequence = 19 digits, which
 * stays inside Int64 until the year 2262.
 */
let __uploadIdSeq = Math.floor(Math.random() * 1000000);
function nextUploadId(fileName) {
  __uploadIdSeq = (__uploadIdSeq + 1) % 1000000;
  return `${Date.now()}${String(__uploadIdSeq).padStart(6, '0')}_${fileName}`;
}
const dicomExtensions = ['dcm', 'dicom']; // Add other DICOM file extensions if needed

const request = indexedDB.open(dbName, dbVersion);

request.onerror = (event) => {
  console.error('IndexedDB error:', event.target.error);
};

request.onsuccess = (event) => {
  db = event.target.result;
};

request.onupgradeneeded = (event) => {
  const db = event.target.result;
  const objectStore = db.createObjectStore('files', { keyPath: 'id' });

};


async function uploadChunk(chunkData, url, token) {
  try {
    const response = await fetch(`${url}/api/StudyFiles`, {
      method: 'POST',
      body: chunkData,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // Check if the response is a bad request
    if (response.status === 400) {
      let errorResult = await response.json();
      
      return {
        success: false,
        error: errorResult,
        isRequestError: false,
        isPause: false,
        isCancelled: false
      };
    }

    // Check for other non-200 responses
    if (!response.ok) {
      return {
        success: false,
        error: response.statusText,
        isRequestError: true,
        isPause: false,
        isCancelled: false
      };
    }

    // Handle the 200 response
    const result = await response.json();
    if (result.hasOwnProperty("isPaused")) {
      if (result.isPaused) {
        return {
          success: false,
          error: "",
          isRequestError: false,
          isPause: true,
          isCancelled: false
        };
      } else if (result.isCancelled) {
        return {
          success: false,
          error: "",
          isRequestError: false,
          isPause: false,
          isCancelled: true
        };
      }
    }

    return {
      success: true,
      error: "",
      isRequestError: false,
      isPause: false,
      isCancelled: false,
      studyFile: result // Return the StudyFileDto object
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      isRequestError: true,
      isPause: false,
      isCancelled: false
    };
  }
}


async function uploadFileChunks(file, url, token, isSingleFile) {
  let lastChunkNumber = file.lastChunkNumber;
  if (!lastChunkNumber) {
    lastChunkNumber = 1;
  } else {
    lastChunkNumber = parseInt(lastChunkNumber) + 1; // if file is already uploaded some chunks and continue from last stage, so plus one in lastChunkNumber to continue upload further chunks
  }

  const totalChunks = Math.ceil(file.file.size / chunkSize);
  let success = false;
  let studyId = 0;
  let studyFile = {};
  for (let i = lastChunkNumber; i <= totalChunks; i++) {
    const start = (i - 1) * chunkSize;
    const end = i * chunkSize;
    const chunkBlob = file.file.slice(start, end);
    const formData = new FormData();
    formData.append('chunkNumber', i);
    formData.append('totalChunks', totalChunks);
    // Declared whole-file length. The API asserts the reassembled bytes match this exactly and
    // rejects the upload otherwise, so a damaged reassembly can never be stored as a DICOM.
    formData.append('totalFileSize', file.file.size);
    formData.append('fileId', file.id);
    formData.append('saleId', file.saleId);
    formData.append('horseId', file.horseId);
    formData.append('studyId', file.studyId);
    formData.append('isAdditional', file.isAdditional);
    formData.append('mediaCategoryId', file.mediaCategoryId);
    formData.append('chunkData', chunkBlob);

    postMessage({ fileId: file.id, chunkNumber: i, action: 'save', totalChunks: totalChunks });
    //storeLastChunkNumber(file.id, i);
    const response = await uploadChunk(formData, url, token);
    success = response.success;
    studyFile = response.studyFile;
    studyId = response.studyFile?.studyId;
    if (!success && response.isRequestError) {
      postMessage({ action: 'check-connectivity' })
      break;
    } else if (!success && response.isCancelled) {
      //postMessage({ fileId: file.id, saleId: file.saleId, horseId: file.horseId, studyId: file.studyId, action: 'delete' });
      break;
    } else if (!success && response.isPause) {
      postMessage({ action: 'pause-file', fileId: file.id })
      break;
    } else if (!success && !response.isRequestError) {
      console.log(response.error)
      postMessage({ action: 'custom-errors', error: response.error, fileId: file.id, isSingleFile: isSingleFile })
      break;
    }

  }
  if (success) {
    // All chunks uploaded successfully, delete the entry from
    let successResponse = { fileId: file.id, saleId: file.saleId, horseId: file.horseId, studyId: file.studyId, studyInstanceUID: file.studyInstanceUID, batchId: file.batchId, studyFile: studyFile, action: 'delete' };
    if (isSingleFile) {
      successResponse = { fileId: file.id, saleId: file.saleId, horseId: file.horseId, studyId: file.studyId, studyInstanceUID: file.studyInstanceUID, batchId: file.batchId, obtainedStudyId: studyId, isSingleFile: isSingleFile, studyFile: studyFile, action: 'delete' };
    }
    postMessage(successResponse);
  }
}

function storeLastChunkNumber(fileId, lastChunkNumber) {

  lastChunkNumber = parseInt(lastChunkNumber);

  let fileInfo = localStorage.getItem(`${fileId}_fileInfo`);
  if (fileInfo) {
    fileInfo = JSON.parse(fileInfo);
    fileInfo.lastChunkNumber = lastChunkNumber.toString();
    localStorage.setItem(`${fileId}_fileInfo`, JSON.stringify(fileInfo));
  } else {
    fileInfo = {
      lastChunkNumber: lastChunkNumber.toString()
    };
    localStorage.setItem(`${fileId}_fileInfo`, JSON.stringify(fileInfo));
  }

}
function setFilePauseState(fileId, isPause) {
 
  let fileInfo = localStorage.getItem(`${fileId}_fileInfo`);
  if (fileInfo) {
    fileInfo = JSON.parse(fileInfo);
    fileInfo.isPause = isPause;
    localStorage.setItem(`${fileId}_fileInfo`, JSON.stringify(fileInfo));
  } else {
    fileInfo = {
      isPause: isPause
    };
    localStorage.setItem(`${fileId}_fileInfo`, JSON.stringify(fileInfo));
  }

}
async function deleteLastChunkNumber(fileId, batchId, studyId) {
  try {
    const transaction = db.transaction(['files'], 'readwrite');
    const objectStore = transaction.objectStore('files');

    // Delete the file by fileId
    await new Promise((resolve, reject) => {
      const request = objectStore.delete(fileId);
      request.onsuccess = resolve;
      request.onerror = (event) => reject(`Error deleting last chunk number for file ${fileId}: ${event.target.error}`);
    });

    // Remove last chunk number from localStorage
    localStorage.removeItem(`${fileId}_fileInfo`);

    if (batchId && studyId) {
      const updateTransaction = db.transaction(['files'], 'readwrite');
      const updateObjectStore = updateTransaction.objectStore('files');

      let updatePromises = [];

      await new Promise((resolve, reject) => {
        const getRequest = updateObjectStore.openCursor();

        getRequest.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            // Update data
            const updateData = cursor.value;
            if (cursor.value.batchId === batchId) {
              updateData.studyId = studyId;
            }

            // Create a promise for the update request
            const updatePromise = new Promise((resolve, reject) => {
              const updateRequest = cursor.update(updateData);
              updateRequest.onsuccess = resolve;
              updateRequest.onerror = (event) => reject(`Error updating studyId for file ${fileId}: ${event.target.error}`);
            });

            updatePromises.push(updatePromise);
            cursor.continue();
          } else {
            // All cursors processed
            resolve();
          }
        };

        getRequest.onerror = (event) => reject(`Error retrieving file with studyInstanceUID ${studyInstanceUID}: ${event.target.error}`);
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);
    }

    return fileId; // Resolve with the fileId when all operations are done

  } catch (error) {
    console.error(error);
    throw error; // Reject the promise with the error
  }
}



function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, dbVersion);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      resolve(request.result);
    }

  });
}
function setFileError(fileId, error) {
  let fileInfo = localStorage.getItem(`${fileId}_fileInfo`);
  if (fileInfo) {
    fileInfo = JSON.parse(fileInfo);
    fileInfo.error = error;
    localStorage.setItem(`${fileId}_fileInfo`, JSON.stringify(fileInfo));
  } else {
    fileInfo = {
      error: error
    };
    localStorage.setItem(`${fileId}_fileInfo`, JSON.stringify(fileInfo));
  }
}
function getFileError(fileId) {
  let fileInfo = localStorage.getItem(`${fileId}_fileInfo`);
  if (fileInfo) {
    fileInfo = JSON.parse(fileInfo);
  }
  return fileInfo?.error;
}

async function getAllFiles() {
  const db = await openDatabase();
  const transaction = db.transaction(['files'], 'readonly');
  const objectStore = transaction.objectStore('files');

  return new Promise((resolve, reject) => {
    const request = objectStore.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const files = request.result?.map((file) => {
        const fileInfo = JSON.parse(localStorage.getItem(`${file.id}_fileInfo`));
        const totalChunks = Math.ceil(file.file.size / chunkSize);
        file.lastChunkNumber = fileInfo && fileInfo.hasOwnProperty("lastChunkNumber") ? fileInfo.lastChunkNumber : 0;
        const isPause = fileInfo?.hasOwnProperty('isPause') ? fileInfo.isPause : file.lastChunkNumber ? true : false;
        file.isPause = isPause;
        
        file.progress = calculatePercentage(totalChunks, file.lastChunkNumber);
        file.error = getFileError(file.id);
        return file;
      });
      resolve(files);
    }
  });
}
function calculatePercentage(totalChunks, uploadedChunkNumber) {
  if (totalChunks === 0) {
    return 0; // Prevent division by zero
  }
  return Math.round((uploadedChunkNumber / totalChunks) * 100);
}
function getTotalFilesCountUploadingStarted() {
  
  let countStartedFiles = 0;

  // Iterate through localStorage keys
  for (let i = 0; i < localStorage.length; i++) {
    // Get the key at index i
    const key = localStorage.key(i);

    // Check if the key is of the format `${fileId}_lastChunkNumber`
    if (key.includes('_fileInfo')) {
      // Extract the fileId from the key
      const fileId = key.replace("_fileInfo", "")
      const fileInfo = JSON.parse(localStorage.getItem(`${fileId}_fileInfo`));

      // Check if there is a corresponding `${fileId}_file_error` key
      if (!fileInfo?.error && !fileInfo.isPause) {
        // Increment count if the file does not have an error
        countStartedFiles++;
      }
    }
  }

  return countStartedFiles;
}
function isDicomFile(fileName) {
  const extension = fileName.split('.').pop().toLowerCase();
  return dicomExtensions.includes(extension);
}
async function getStudyInstanceUID(file) {

  if (isDicomFile(file.name)) {

    return new Promise(async (resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const byteArray = new Uint8Array(arrayBuffer);
          const dataSet = dicomParser.parseDicom(byteArray);
          const studyInstanceUID = dataSet.string('x0020000d');
          resolve(studyInstanceUID);
        } catch (error) {
          reject(`Failed to parse DICOM file: ${error}`);
        }
      };
      reader.onerror = () => {
        reject('Failed to read file');
      };
      await reader.readAsArrayBuffer(file);
    });
  } else {
    //throw new Error('Invalid DICOM file extension');
  }
}
async function updateFilesWithUID(files) {
  const updatedFiles = await Promise.all(files.map(async (file) => {
    let uid = "";
    try {
      uid = await getStudyInstanceUID(file.file);
    } catch (e) {
      console.error('Error fetching StudyInstanceUID:', e);
      // Handle the error as needed
    }
    return { ...file, studyInstanceUID: uid };
  }));

  return updatedFiles;
}
function addFiles(files, callback) {
  
  updateFilesWithUID(files).then(async (updatedFiles) => {
    
    const transaction = db.transaction(['files'], 'readwrite');
    const objectStore = transaction.objectStore('files');
    const batchId = getBatchId();
    let firsStudyInstanceUID = "";
    const fileWithUID = updatedFiles.filter(x => x.studyInstanceUID);
    if (fileWithUID.length > 0) {
      firsStudyInstanceUID = fileWithUID[0].studyInstanceUID;
    }
    updatedFiles.forEach((file, index) => {
      let studyInstanceUID = file.studyInstanceUID;
      if (!isDicomFile(file.file.name)) {
        studyInstanceUID = firsStudyInstanceUID;
      }
      objectStore.add({
        id: nextUploadId(file.file.name),
        file: file.file,
        batchId: batchId,
        studyInstanceUID: studyInstanceUID,
        saleId: file.saleId,
        horseId: file.horseId,
        isAdditional: file.isAdditional,
        mediaCategoryId: file.mediaCategoryId,
        studyId: file.studyId,
        saleName: file.saleName,
        horseName: file.horseName,
        lot: file.lot
      });
    });

    transaction.oncomplete = async () => {

      const files = await getAllFiles();
      callback(files);
      //await Promise.all(files.map(uploadFileChunks))
    }
  }).catch(error => {
    console.error('Error processing files:', error);
  });

}

function getBatchId() {
  var currentTime = new Date().getTime();

  // 10,000 ticks in 1 millisecond
  // jsTicks is number of ticks from midnight Jan 1, 1970
  var jsTicks = currentTime * 10000;

  // add 621355968000000000 to jsTicks
  // netTicks is number of ticks from midnight Jan 1, 01 CE
  var netTicks = jsTicks + 621355968000000000;
  return netTicks;
}

function getDateFromTicks(netTicks) {
  // Subtract 621355968000000000 to get the ticks from 01 Jan 01 CE
  var jsTicks = netTicks - 621355968000000000;

  // Convert back to milliseconds by dividing by 10,000
  var currentTime = jsTicks / 10000;

  // Create a new Date object using the milliseconds
  var date = new Date(currentTime);

  return date;
}

function backgroundSync() {

  navigator.serviceWorker.ready
    .then(function (registration) {



      return registration.sync.register('syncData');
    })
    .then(function () {
      console.log('Sync event registered');
    })
    .catch(function (error) {
      console.error('Sync registration failed:', error);
    });

}

